import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Undo2 } from 'lucide-react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormField,
} from '@afrilink/ui';
import {
  adminBillingService,
  type AdminPaymentRow,
  type RecordRefundResult,
  type RefundEfError,
} from '@/domains/billing/services/adminBillingService';
import { formatZAR } from '@/shared/utils/format';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: AdminPaymentRow | null;
  /** Called after a successful record so the parent can update the row. */
  onRecorded: (paymentId: string, result: RecordRefundResult) => void;
}

export function RecordRefundModal({ open, onOpenChange, payment, onRecorded }: Props) {
  const [kind, setKind] = useState<'full' | 'partial' | 'goodwill_credit'>('full');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'payfast_portal' | 'eft' | 'credit_balance'>('payfast_portal');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [tokenWarning, setTokenWarning] = useState<string[] | null>(null);
  const [tokenAck, setTokenAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RecordRefundResult | null>(null);

  function reset() {
    setKind('full');
    setAmount('');
    setMethod('payfast_portal');
    setReference('');
    setReason('');
    setTokenWarning(null);
    setTokenAck(false);
    setError(null);
    setResult(null);
  }

  function close() {
    onOpenChange(false);
    setTimeout(reset, 300);
  }

  async function submit() {
    if (!payment) return;
    setBusy(true);
    setError(null);
    try {
      const res = await adminBillingService.recordRefund({
        payment_id: payment.id,
        refund_kind: kind,
        amount: kind === 'full' ? undefined : Number(amount),
        method,
        external_reference: reference.trim() || undefined,
        reason: reason.trim(),
        cancel_subscription_ack: tokenAck || undefined,
      });
      setResult(res);
      onRecorded(payment.id, res);
    } catch (e) {
      const err = e as RefundEfError;
      if (err.code === 'live_subscription_token') {
        setTokenWarning(err.liveTokens ?? ['a recurring subscription']);
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  const amountValid =
    kind === 'full' ||
    (Number(amount) > 0 && !!payment && Number(amount) <= payment.amount);
  const canSubmit =
    !busy && !!payment && reason.trim().length > 0 && amountValid &&
    (!tokenWarning || tokenAck);

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && close()}
      title="Record refund"
      description="Records a refund that has already been processed manually (PayFast dashboard, EFT, or account credit). No money moves from here."
    >
      <ModalBody>
        {result ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-md border border-success/30 bg-success/5 p-3 text-sm text-success">
              <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />
              <span>
                {result.refund_kind === 'full'
                  ? `Full refund of ${formatZAR(result.amount)} recorded — payment and invoice are now refunded.`
                  : `Credit note ${result.credit_note_number} issued for ${formatZAR(result.amount)}.`}
              </span>
            </div>
            {result.emailed_to && (
              <p className="text-xs text-muted-foreground">
                Confirmation emailed to {result.emailed_to}.
              </p>
            )}
          </div>
        ) : !payment ? null : (
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <span className="text-muted-foreground">Payment</span>{' '}
              <span className="font-semibold">{formatZAR(payment.amount)}</span>
              {payment.invoice_number && (
                <>
                  {' '}on <code className="text-xs">{payment.invoice_number}</code>
                </>
              )}{' '}
              <span className="text-muted-foreground">· {payment.gateway}</span>
            </div>

            <FormField label="Refund kind" htmlFor="refund-kind">
              <select
                id="refund-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
                className="h-10 w-full rounded border border-input bg-card px-3 text-sm"
              >
                <option value="full">Full refund — flips payment + invoice to refunded</option>
                <option value="partial">Partial — issues a credit note</option>
                <option value="goodwill_credit">Goodwill credit — issues a credit note</option>
              </select>
            </FormField>

            {kind !== 'full' && (
              <FormField label={`Amount (max ${formatZAR(payment.amount)})`} htmlFor="refund-amount" required>
                <Input
                  id="refund-amount"
                  type="number"
                  min={0.01}
                  max={payment.amount}
                  step={0.01}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              </FormField>
            )}

            <FormField label="Money moved via" htmlFor="refund-method">
              <select
                id="refund-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as typeof method)}
                className="h-10 w-full rounded border border-input bg-card px-3 text-sm"
              >
                <option value="payfast_portal">PayFast dashboard refund (card reversal)</option>
                <option value="eft">EFT to the customer's bank account</option>
                <option value="credit_balance">Credit on the customer's account</option>
              </select>
            </FormField>

            <FormField label="External reference" htmlFor="refund-ref" helper="PayFast refund id / EFT reference — optional but recommended.">
              <Input
                id="refund-ref"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                maxLength={120}
              />
            </FormField>

            <FormField label="Reason" htmlFor="refund-reason" required>
              <Input
                id="refund-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. 30-day money-back guarantee — cancelled 12 days in"
                maxLength={300}
              />
            </FormField>

            {tokenWarning && (
              <div className="rounded-md border border-warning/40 bg-warning/5 p-3">
                <div className="flex items-start gap-2 text-sm">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
                  <div>
                    <p className="font-semibold text-navy-primary">
                      Live PayFast Recurring token: {tokenWarning.join(', ')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      If this refund ends the relationship, cancel the recurring billing on the
                      PayFast dashboard FIRST — otherwise the customer keeps getting charged.
                    </p>
                    <label className="mt-2 flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={tokenAck}
                        onChange={(e) => setTokenAck(e.target.checked)}
                      />
                      I've cancelled the token (or confirmed it should keep running)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">
                {error}
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="outline" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canSubmit} loading={busy}>
              <Undo2 className="h-4 w-4" />
              Record refund
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default RecordRefundModal;
